import { useState } from 'react';
import styled from 'styled-components';
import { Button, Flex, Popover, Tag, Tooltip, Typography } from 'antd';
import { LuCheck, LuCircleDot, LuLasso, LuSquare } from 'react-icons/lu';

import { DETECTION_POINTER, DETECTION_POINTER_LABELS, DETECTION_POINTERS } from '../constants';

const { Text } = Typography;

/**
 * Glyphs echo how each shape draws on the map — the trigger doubles as the row's
 * shape indicator, so the tree reads as a legend.
 *
 * Outline uses the lasso rather than a circle on purpose: a plain circle differs
 * from the center point's ring-and-dot by a couple of pixels, which is invisible
 * at the size these render, and the two shapes are the ones users most need to
 * tell apart. The lasso is the familiar "trace an irregular region" metaphor and
 * is unmistakable at any size.
 */
const POINTER_ICONS = {
	[DETECTION_POINTER.CENTER_POINT]: LuCircleDot,
	[DETECTION_POINTER.BOUNDING_BOX]: LuSquare,
	[DETECTION_POINTER.OUTLINE]: LuLasso,
};

const PointerOption = styled.button`
	display: flex;
	align-items: center;
	gap: 8px;
	width: 100%;
	padding: 6px 8px;
	border: none;
	border-radius: 4px;
	background: transparent;
	color: inherit;
	text-align: left;
	cursor: ${p => (p.disabled ? 'not-allowed' : 'pointer')};
	opacity: ${p => (p.disabled ? 0.45 : 1)};

	&:hover:not(:disabled) {
		background: rgba(128, 128, 128, 0.15);
	}
`;

/**
 * Per-object detection-pointer picker — the control that decides whether this
 * object's detections draw as center points, bounding boxes, or outlines.
 *
 * Users see this as "shape"; `detection_pointer` is the wire field and the code's
 * name for it, never UI copy — the split `DETECTION_POINTER_LABELS` already makes.
 *
 * Picking saves immediately; there is no Apply. The trigger doubles as the row's
 * shape indicator, so the tree reads as a legend of what's on the map.
 *
 * A shape the current run has no data for is offered but disabled, rather than
 * hidden: a user who set an object to Outline and sees boxes needs to be told
 * the model didn't produce outlines, not left guessing. Partial availability
 * stays selectable and says how many detections it covers.
 *
 * @param {object} props
 * @param {string} props.objectKey
 * @param {string} props.label - object display name, for accessible labels
 * @param {string} props.pointer - the effective pointer (saved override or default)
 * @param {string} props.defaultPointer - the taxonomy's recommendation
 * @param {{ total: number }} [props.availability] - per-shape counts for this object
 * @param {string} [props.color] - the object's color, so the trigger matches the map
 * @param {(objectKey: string, pointer: string) => void} props.onChange
 */
const DetectionPointerPicker = ({
	objectKey,
	label,
	pointer,
	defaultPointer,
	availability,
	color,
	onChange,
}) => {
	const [open, setOpen] = useState(false);

	const handlePick = next => {
		setOpen(false);
		if (next !== pointer) onChange(objectKey, next);
	};

	const TriggerGlyph = POINTER_ICONS[pointer];

	const content = (
		<Flex vertical gap={2} style={{ minWidth: 208 }}>
			<Text
				type="secondary"
				style={{
					fontSize: '0.7rem',
					textTransform: 'uppercase',
					letterSpacing: '0.06em',
					padding: '0 8px 4px',
				}}>
				Shape
			</Text>

			{DETECTION_POINTERS.map(option => {
				const Glyph = POINTER_ICONS[option];
				const total = availability?.total ?? 0;
				const count = availability?.[option] ?? 0;
				// With no counts yet, don't block the choice.
				const unavailable = total > 0 && count === 0;
				const partial = count > 0 && count < total;

				return (
					<PointerOption
						key={option}
						type="button"
						disabled={unavailable}
						onClick={() => handlePick(option)}
						aria-label={`${DETECTION_POINTER_LABELS[option]} for ${label}`}>
						<Glyph aria-hidden />
						<Text style={{ flex: 1, fontSize: '0.8rem' }}>
							{DETECTION_POINTER_LABELS[option]}
						</Text>

						{option === defaultPointer && (
							<Tag
								variant="filled"
								style={{ fontSize: '0.6rem', margin: 0, lineHeight: 1.6 }}>
								DEFAULT
							</Tag>
						)}

						{unavailable && (
							<Text type="secondary" style={{ fontSize: '0.65rem' }}>
								not in this run
							</Text>
						)}
						{partial && (
							<Text type="secondary" style={{ fontSize: '0.65rem' }}>
								{count}/{total}
							</Text>
						)}

						{option === pointer && <LuCheck aria-hidden />}
					</PointerOption>
				);
			})}
		</Flex>
	);

	return (
		<Popover
			content={content}
			trigger="click"
			placement="bottomLeft"
			open={open}
			onOpenChange={next => setOpen(next)}
			styles={{ body: { padding: 8 } }}>
			<Tooltip title={`Shape: ${DETECTION_POINTER_LABELS[pointer]}`}>
				<Button
					type="text"
					size="small"
					aria-label={`Shape for ${label}`}
					icon={
						<span style={{ display: 'inline-flex', alignItems: 'center', color }}>
							<TriggerGlyph />
						</span>
					}
				/>
			</Tooltip>
		</Popover>
	);
};

export default DetectionPointerPicker;
